import React, { useState, useEffect, useCallback } from 'react';
import emailOutreachApi from '../api/emailOutreachApi';
import './ReplyComposerModal.css';

function ReplyComposerModal({ isOpen, onClose, reply, mailboxes = [], onSuccess }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [instructions, setInstructions] = useState('');
  const [reasoning, setReasoning] = useState('');
  const [selectedMailboxId, setSelectedMailboxId] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // Load approved templates for quick response snippets
  useEffect(() => {
    let mounted = true;
    if (isOpen) {
      emailOutreachApi
        .listTemplates({ status: 'approved' })
        .then((res) => {
          if (mounted && res?.success && Array.isArray(res.data)) {
            setAvailableTemplates(res.data);
          }
        })
        .catch((err) => console.warn('Failed to load templates in reply composer:', err));
    }
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  const handleApplyTemplate = (templateId) => {
    if (!templateId) return;
    const tmpl = availableTemplates.find((t) => t.id === templateId);
    if (!tmpl) return;

    const prospectName =
      reply?.from_name || (reply?.from_email ? reply.from_email.split('@')[0] : 'there');
    const templatedBody = (tmpl.body_template || '')
      .split('{{firstName}}')
      .join(prospectName)
      .split('{{name}}')
      .join(prospectName)
      .split('{{companyName}}')
      .join('your team');

    setBody(templatedBody);
    setReasoning(`Applied governed template: ${tmpl.name} (v${tmpl.version})`);
  };

  // Default subject based on reply subject
  const getDefaultSubject = useCallback((origSubject) => {
    if (!origSubject) return 'Re: Inquiry';
    return origSubject.toLowerCase().startsWith('re:') ? origSubject : `Re: ${origSubject}`;
  }, []);

  // Fetch or generate AI draft
  const handleGenerateDraft = useCallback(
    async (customGuidance = '') => {
      if (!reply?.id) return;
      try {
        setGenerating(true);
        setError(null);
        const res = await emailOutreachApi.generateReplyDraft(reply.id, {
          instructions: customGuidance,
        });

        if (res.success && res.data) {
          setSubject(res.data.draftSubject || getDefaultSubject(reply.subject));
          setBody(res.data.draftBody || '');
          setReasoning(res.data.reasoning || '');
        }
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to generate AI draft');
      } finally {
        setGenerating(false);
      }
    },
    [reply?.id, reply?.subject, getDefaultSubject]
  );

  // Initialize modal state on open
  useEffect(() => {
    if (isOpen && reply) {
      setError(null);
      setInstructions('');
      setReasoning('');

      // Auto-select mailbox: match reply's connection_id or first connected
      const connected = mailboxes.filter((m) => m.status === 'connected');
      const match = connected.find((m) => m.id === reply.connection_id) || connected[0];
      setSelectedMailboxId(match ? match.id : '');

      // Set initial subject
      setSubject(getDefaultSubject(reply.subject));
      setBody('');

      // Only trigger automatic AI draft if explicitly requested by caller
      if (reply.autoGenerate) {
        handleGenerateDraft('');
      }
    }
  }, [isOpen, reply, mailboxes, getDefaultSubject, handleGenerateDraft]);

  if (!isOpen || !reply) return null;

  const connectedMailboxes = mailboxes.filter((m) => m.status === 'connected');

  const handleSend = async () => {
    if (!subject.trim()) {
      setError('Please provide a subject line');
      return;
    }
    if (!body.trim()) {
      setError('Email body cannot be empty');
      return;
    }

    try {
      setSending(true);
      setError(null);
      const res = await emailOutreachApi.sendReply(reply.id, {
        connectionId: selectedMailboxId || undefined,
        subject: subject.trim(),
        body: body.trim(),
      });

      if (res.success) {
        if (onSuccess) onSuccess(res.data || res);
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="eo-modal-overlay" role="dialog" aria-modal="true">
      <div className="eo-modal-container">
        {/* Header */}
        <div className="eo-modal-header">
          <div className="eo-modal-title-group">
            <div className="eo-modal-title-row">
              <h3>Reply Assistant</h3>
              <span className="eo-modal-ai-tag">
                <i className="fas fa-magic"></i> FR-M07-002
              </span>
            </div>
            <p className="eo-modal-sub">
              Contextual AI reply generation with pre-send suppression governance.
            </p>
          </div>
          <button
            className="eo-modal-close-btn"
            onClick={onClose}
            disabled={sending}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="eo-modal-body">
          {error && (
            <div className="eo-alert eo-alert-error">
              <i className="fas fa-exclamation-triangle"></i>
              <span>{error}</span>
              <button className="eo-alert-close" onClick={() => setError(null)}>
                ×
              </button>
            </div>
          )}

          {/* Inbound Prospect Context */}
          <div className="eo-context-card">
            <div className="eo-context-row">
              <span className="eo-context-recipient">
                <strong>Prospect:</strong> {reply.from_email || 'Unknown Contact'}
              </span>
              {reply.effective_classification && (
                <span className="eo-disposition-pill eo-pill-neutral">
                  Disposition: {reply.effective_classification.replace(/_/g, ' ')}
                </span>
              )}
            </div>

            {reply.recommended_action && (
              <div className="eo-context-recommendation">
                <i className="fas fa-lightbulb"></i>
                <div>
                  <strong>AI Next Action:</strong> {reply.recommended_action}
                </div>
              </div>
            )}

            {reply.snippet && <p className="eo-context-snippet">&ldquo;{reply.snippet}&rdquo;</p>}
          </div>

          {/* Response Methods Strip (Template or AI) */}
          <div className="eo-reply-methods-strip">
            <div className="eo-reply-method-item">
              <label className="eo-method-label">
                <i className="fas fa-file-contract" style={{ color: '#2563eb' }}></i> Apply Approved
                Template:
              </label>
              <select
                className="eo-select"
                onChange={(e) => {
                  handleApplyTemplate(e.target.value);
                  e.target.value = '';
                }}
                defaultValue=""
                disabled={sending || generating}
              >
                <option value="" disabled>
                  -- Choose an Approved Template to apply --
                </option>
                {availableTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    [{t.category.toUpperCase().replace(/_/g, ' ')}] {t.name} (v{t.version})
                  </option>
                ))}
              </select>
            </div>

            <div className="eo-reply-method-item">
              <label className="eo-method-label">
                <i className="fas fa-magic" style={{ color: '#7c3aed' }}></i> Or Generate with AI:
              </label>
              <div className="eo-guidance-box">
                <input
                  type="text"
                  className="eo-guidance-input"
                  placeholder="Optional guidance (e.g. 'offer 15% discount', 'propose Friday 3pm')..."
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleGenerateDraft(instructions);
                  }}
                  disabled={generating || sending}
                />
                <button
                  type="button"
                  className="eo-btn-ai-generate"
                  onClick={() => handleGenerateDraft(instructions)}
                  disabled={generating || sending}
                >
                  <i className={`fas fa-magic ${generating ? 'fa-spin' : ''}`}></i>
                  <span>
                    {generating ? 'Generating...' : body ? 'Regenerate Draft' : 'Generate AI Draft'}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Draft Subject */}
          <div className="eo-field-group">
            <label className="eo-field-label">Subject</label>
            <input
              type="text"
              className="eo-field-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
              placeholder="Re: Subject line"
            />
          </div>

          {/* Draft Body */}
          <div className="eo-field-group">
            <label className="eo-field-label">Email Draft (Editable)</label>
            <textarea
              className="eo-field-textarea"
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={sending || generating}
              placeholder={
                generating
                  ? 'Generating contextual AI reply draft...'
                  : 'Type your reply, choose an approved template above, or click Generate AI Draft...'
              }
            />
          </div>

          {/* Reasoning / Strategy Note */}
          {reasoning && (
            <div className="eo-reasoning-banner">
              <i className="fas fa-info-circle"></i>
              <span>
                <strong>Strategy:</strong> {reasoning}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="eo-modal-footer">
          <div className="eo-sender-select-group">
            <label htmlFor="eo-sender-select">Send from:</label>
            <select
              id="eo-sender-select"
              className="eo-sender-select"
              value={selectedMailboxId}
              onChange={(e) => setSelectedMailboxId(e.target.value)}
              disabled={sending || connectedMailboxes.length === 0}
            >
              {connectedMailboxes.length === 0 ? (
                <option value="">No connected mailboxes</option>
              ) : (
                connectedMailboxes.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.account_email} ({m.provider})
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="eo-modal-action-btns">
            <button
              type="button"
              className="eo-btn eo-btn-secondary"
              onClick={onClose}
              disabled={sending}
            >
              Discard
            </button>
            <button
              type="button"
              className="eo-btn eo-btn-primary"
              onClick={handleSend}
              disabled={sending || generating || !body.trim() || connectedMailboxes.length === 0}
            >
              <i className={`fas fa-paper-plane ${sending ? 'fa-spin' : ''}`}></i>
              <span>{sending ? 'Sending...' : 'Send Reply'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReplyComposerModal;
